import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { isStudentManager, managerFamilyId } from '@/auth/familyManager';
import { useMe } from '@/auth/useAuth';
import { useAccountPath } from '@/app/accountSurface';
import { ApiError, api } from '@/lib/api';
import { startHostedCheckout } from './airwallex';

type StudyProduct = { id: string; sku: string; title: string; price_aud_cents: number; access_days: number };
type FamilyKid = { id: string; nickname: string };
type StudyAccess = { id: string; product: { sku: string; title: string }; ends_at: string };
type StudyOrder = {
  id: string;
  order_status: 'creating' | 'pending' | 'fulfilled' | 'refund_due' | 'refunded' | 'failed' | 'cancelled';
  payment: { id: string; status: string; needs_refund: boolean } | null;
  entitlement: { id: string; effective_status: string; ends_at: string } | null;
};
/** SIR-COM-07: a school's parent levy already pays Study for this learner. */
type StudyCoverage = { covered: boolean; institution_name: string | null; ends_at: string | null };
const COVERED_CODE = 'STUDY_FAMILY_PURCHASE_COVERED';
type StudyCheckout = { order_id: string; checkout_url: string | null; payment_intent_id?: string; client_secret?: string };

const formSchema = z.object({ product_id: z.string().min(1, 'Choose a product.'), kid_id: z.string().min(1, 'Choose your child.') });
type FormValues = z.infer<typeof formSchema>;
const ORDER_STORAGE_KEY = 'study-checkout-order';
const REQUEST_STORAGE_KEY = 'study-checkout-request';
const money = (cents: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(cents / 100);

export function StudyPurchasePage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const me = useMe();
  // A parent buys for a child; an independent student buys for themselves (D-SSU-23).
  const familyId = managerFamilyId(me.data);
  const student = isStudentManager(me.data);
  const accountPath = useAccountPath();
  const [pendingOrderId, setPendingOrderId] = useState(() => sessionStorage.getItem(ORDER_STORAGE_KEY));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<FormValues>({ resolver: zodResolver(formSchema), defaultValues: { product_id: '', kid_id: '' } });
  const productId = form.watch('product_id');
  const kidId = form.watch('kid_id');
  const products = useQuery({ queryKey: ['study-commerce-products'], queryFn: () => api<StudyProduct[]>('/study/commerce/products'), retry: false });
  const kids = useQuery({
    queryKey: ['family-kids', familyId],
    queryFn: () => api<FamilyKid[]>(`/families/${familyId}/kids`),
    enabled: !!familyId,
    retry: false,
  });
  const access = useQuery({
    queryKey: ['study-child-access', kidId],
    queryFn: () => api<StudyAccess[]>(`/study/commerce/children/${kidId}/access`),
    enabled: !!kidId,
    retry: false,
  });
  // An older API without this route answers 404: that reads as "not covered", never as an error.
  const coverage = useQuery({
    queryKey: ['study-child-coverage', kidId],
    queryFn: () => api<StudyCoverage>(`/study/commerce/children/${kidId}/coverage`),
    enabled: !!kidId,
    retry: false,
  });
  const order = useQuery({
    queryKey: ['study-commerce-order', pendingOrderId],
    queryFn: () => api<StudyOrder>(`/study/commerce/orders/${pendingOrderId}`),
    enabled: !!pendingOrderId,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.order_status;
      return !status || status === 'creating' || status === 'pending' ? 2_000 : false;
    },
  });

  const clearCheckout = () => {
    sessionStorage.removeItem(ORDER_STORAGE_KEY);
    sessionStorage.removeItem(REQUEST_STORAGE_KEY);
    setPendingOrderId(null);
    setError(null);
  };

  useEffect(() => {
    if (!products.data?.length || productId) return;
    form.setValue('product_id', products.data[0].id);
  }, [form, productId, products.data]);

  useEffect(() => {
    if (!kids.data?.length || kidId) return;
    const requested = searchParams.get('kid_id');
    const selected = kids.data.find((kid) => kid.id === requested) ?? kids.data[0];
    form.setValue('kid_id', selected.id);
  }, [form, kidId, kids.data, searchParams]);

  useEffect(() => {
    if (order.data?.order_status === 'failed' || order.data?.order_status === 'cancelled') {
      clearCheckout();
    }
  }, [order.data?.order_status]);

  if (me.data?.kind === 'user' && !familyId) {
    return <Navigate to="/portal/register" state={{ from: location }} replace />;
  }

  const selectedProduct = products.data?.find((product) => product.id === productId);
  const alreadyActive = !!selectedProduct && !!access.data?.some((grant) => grant.product.sku === selectedProduct.sku);
  const covered = coverage.data?.covered === true ? coverage.data : null;
  const submit = form.handleSubmit(async (values) => {
    if (pendingOrderId || alreadyActive || covered) return;
    setSubmitting(true);
    setError(null);
    const requestKey = sessionStorage.getItem(REQUEST_STORAGE_KEY) ?? crypto.randomUUID();
    sessionStorage.setItem(REQUEST_STORAGE_KEY, requestKey);
    try {
      const checkout = await api<StudyCheckout>('/study/commerce/checkouts', {
        method: 'POST', body: { ...values, request_key: requestKey },
      });
      sessionStorage.setItem(ORDER_STORAGE_KEY, checkout.order_id);
      setPendingOrderId(checkout.order_id);
      if (!checkout.checkout_url) return;
      await startHostedCheckout({
        checkout_url: checkout.checkout_url,
        payment_intent_id: checkout.payment_intent_id,
        client_secret: checkout.client_secret,
      });
    } catch (cause) {
      // The school's parent levy covers this learner: no order exists, so there is nothing to read back.
      if (cause instanceof ApiError && cause.code === COVERED_CODE) {
        sessionStorage.removeItem(REQUEST_STORAGE_KEY);
        setError('Your school already pays for Study for this child. You do not need to buy it.');
        void coverage.refetch();
        return;
      }
      try {
        const recovered = await api<StudyOrder>(`/study/commerce/requests/${requestKey}`);
        if (recovered.payment || recovered.order_status !== 'creating') {
          sessionStorage.setItem(ORDER_STORAGE_KEY, recovered.id);
          setPendingOrderId(recovered.id);
        }
        setError('Checkout status is uncertain. Do not start a different payment; retry or check this order.');
      } catch {
        setError(cause instanceof ApiError ? cause.message : 'Could not confirm checkout. Retry with the same request.');
      }
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="mx-auto w-full max-w-[980px] space-y-6" data-testid="study-purchase-page">
      <header className="rounded-[32px] border border-brand-sky/30 bg-wash-sky p-6 sm:p-9">
        <p className="eyebrow eyebrow-sky">Airbotix Study</p>
        <h1 className="mt-3 text-[32px] font-extrabold text-ink sm:text-[44px]">{student ? 'Study access for you' : 'Study access for your child'}</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink-soft">
          {student
            ? 'Payment and access are confirmed separately. Exam Practice Packs are not included.'
            : 'Choose one child. Payment and access are confirmed separately. Exam Practice Packs are not included.'}
        </p>
      </header>

      {pendingOrderId && order.isError && (
        <section className="card-base" role="alert">
          <h2 className="text-xl font-bold text-ink">We cannot confirm this order yet</h2>
          <p className="mt-2 text-ink-soft">Do not start another payment. Check this order again.</p>
          <button type="button" className="btn-pill-primary mt-4" onClick={() => void order.refetch()}>Check order</button>
          {order.error instanceof ApiError && order.error.status === 404 && (
            <button type="button" className="btn-pill-secondary ml-3 mt-4" onClick={clearCheckout}>Clear unavailable order</button>
          )}
        </section>
      )}
      {order.data?.order_status === 'fulfilled' && order.data.entitlement?.effective_status === 'active' && (
        <section className="card-base" data-testid="study-purchase-ready">
          <CheckCircle2 className="text-brand-mint" aria-hidden="true" />
          <h2 className="mt-2 text-2xl font-bold text-ink">Study access is active</h2>
          <p className="mt-2 text-ink-soft">{student ? 'You' : 'The selected child'} can use Study until {new Date(order.data.entitlement.ends_at).toLocaleDateString('en-AU')}.</p>
          {student ? (
            <Link to="/learn/study" className="btn-pill-primary mt-5 inline-block">Open Study</Link>
          ) : (
            <>
              <Link to={accountPath('/portal')} className="btn-pill-primary mt-5 inline-block">Back to family Home</Link>
              <button type="button" className="btn-pill-secondary ml-3 mt-5" onClick={clearCheckout}>Purchase for another child</button>
            </>
          )}
        </section>
      )}
      {order.data?.order_status === 'fulfilled' && order.data.entitlement?.effective_status !== 'active' && (
        <section className="card-base" role="status">
          <h2 className="text-xl font-bold text-ink">This Study access is no longer active</h2>
          <p className="mt-2 text-ink-soft">The earlier payment remains in your order history. A new purchase creates a separate order.</p>
          <button type="button" className="btn-pill-secondary mt-4" onClick={clearCheckout}>Start a new purchase</button>
        </section>
      )}
      {order.data?.order_status === 'refunded' && (
        <section className="card-base" role="status">
          <h2 className="text-xl font-bold text-ink">This order was refunded</h2>
          <button type="button" className="btn-pill-secondary mt-4" onClick={clearCheckout}>Start a new purchase</button>
        </section>
      )}
      {order.data?.payment?.status === 'succeeded' && order.data?.order_status !== 'fulfilled' && (
        <section className="card-base" role="status" data-testid="study-purchase-paid-no-access">
          <h2 className="text-xl font-bold text-ink">Payment received; access is not active</h2>
          <p className="mt-2 text-ink-soft">Do not pay again. This order needs review before {student ? 'you can' : 'your child can'} use it.</p>
          <button type="button" className="btn-pill-secondary mt-4" onClick={() => void order.refetch()}>Check again</button>
        </section>
      )}
      {pendingOrderId && (order.isLoading || order.data?.order_status === 'pending' || order.data?.order_status === 'creating') && (
        <section className="card-base" role="status" data-testid="study-purchase-pending">
          <h2 className="text-xl font-bold text-ink">Waiting for payment confirmation</h2>
          <p className="mt-2 text-ink-soft">Keep this order open. Access starts only after payment is confirmed.</p>
        </section>
      )}

      {!pendingOrderId && (
        <section className="card-base">
          <h2 className="text-2xl font-bold text-ink">{student ? 'Choose your Study plan' : 'Choose the learner'}</h2>
          {products.isLoading || kids.isLoading ? <p className="mt-4 text-ink-soft">Loading your options…</p> : null}
          {products.isError || kids.isError ? <p role="alert" className="mt-4 text-brand-coral">Could not load purchase options.</p> : null}
          {products.data?.length === 0 && <p className="mt-4 text-ink-soft">Study access is not available to purchase yet.</p>}
          {!!products.data?.length && !!kids.data?.length && (
            <form onSubmit={submit} className="mt-5 space-y-5">
              <label className="block text-sm font-bold text-ink">Study product
                <select {...form.register('product_id')} className="mt-2 w-full rounded-2xl border border-hairline bg-canvas-pure px-4 py-3">
                  {products.data.map((product) => <option key={product.id} value={product.id}>{product.title} · {money(product.price_aud_cents)} · {product.access_days} days</option>)}
                </select>
              </label>
              <label className="block text-sm font-bold text-ink">{student ? 'Learner' : 'Your child'}
                <select {...form.register('kid_id')} className="mt-2 w-full rounded-2xl border border-hairline bg-canvas-pure px-4 py-3">
                  {kids.data.map((kid) => <option key={kid.id} value={kid.id}>{kid.nickname}</option>)}
                </select>
              </label>
              {alreadyActive && <p role="status" className="rounded-2xl bg-wash-mint p-4 text-ink">{student ? 'You already have' : 'This child already has'} active Study access.</p>}
              {covered && (
                <p role="status" className="rounded-2xl bg-wash-mint p-4 text-ink" data-testid="study-purchase-covered">
                  {covered.institution_name ?? 'Your school'} already pays for Study for this child through school fees
                  {covered.ends_at ? ` until ${new Date(covered.ends_at).toLocaleDateString('en-AU')}` : ''}. You do not need to buy it.
                </p>
              )}
              {error && <p role="alert" className="text-brand-coral">{error}</p>}
              <button type="submit" className="btn-pill-primary" disabled={submitting || alreadyActive || !!covered || access.isLoading || coverage.isLoading || !selectedProduct || !kidId}>
                {submitting ? 'Opening checkout…' : 'Continue to secure checkout'}
              </button>
            </form>
          )}
          <p className="mt-5 flex items-start gap-2 text-xs text-slate2"><ShieldCheck className="h-4 w-4 shrink-0 text-brand-sky" aria-hidden="true" />Airwallex handles payment details. Airbotix confirms the order before granting access.</p>
        </section>
      )}
    </div>
  );
}
